import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import { useToastStore } from '../../../store/toast.store';
import * as usersService from '../services/users.service';
import type {
  AdminUserDetailDTO,
  AdminUserReleaseAppleVerificationResponseDTO,
  AdminWalletAdjustmentParams,
  AdminUserStatusUpdateResponseDTO,
  AdminUserUpdateParams,
  UserStatus,
} from '../types/users.types';

const ERROR_MAP: Record<string, string> = {
  admin_role_required: '无管理员权限',
  'invalid user id': '无效的用户 ID',
  'user not found': '用户不存在',
  'invalid user status': '状态值不合法',
  'status change reason is required': '请填写状态变更原因',
  'user email is required': '请输入有效邮箱',
  'user password is too short': '密码至少 8 位',
  'invalid admin account target': '只能在这里编辑普通会员账号',
  'apple verification not found': '当前账号没有 Apple 绑定记录',
  'user already exists': '邮箱已存在',
  'invalid wallet adjustment type': '调整类型不合法',
  'invalid wallet adjustment amount': '请输入有效的调整金额',
  'wallet adjustment remark is required': '请填写调整备注',
  'wallet balance is insufficient': '可用余额不足，无法扣款',
  network_error: '网络连接失败，请检查网络',
};

function resolveError(error: unknown): string {
  if (isApiError(error)) {
    return ERROR_MAP[error.message] ?? `请求失败 (${error.message})`;
  }
  return '未知错误';
}

export function useAdminUserDetail(userId: number) {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<AdminUserDetailDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<AdminUserStatusUpdateResponseDTO | null>(null);
  const [lastReleasedAppleVerification, setLastReleasedAppleVerification] = useState<AdminUserReleaseAppleVerificationResponseDTO | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(userId) || userId <= 0) {
      setData(null);
      setError('无效的用户 ID');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await usersService.getAdminUserDetail(userId);
      setData(result);
    } catch (e) {
      setError(resolveError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateStatus = useCallback(async (status: UserStatus, reason: string) => {
    if (!Number.isFinite(userId) || userId <= 0) {
      return false;
    }
    setUpdating(true);
    try {
      const result = await usersService.updateAdminUserStatus(userId, { status, reason });
      setLastUpdate(result);
      addToast('success', status === 'disabled' ? '用户已禁用' : '用户已恢复正常');
      await load();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setUpdating(false);
    }
  }, [addToast, load, userId]);

  const updateUser = useCallback(async (payload: AdminUserUpdateParams) => {
    if (!Number.isFinite(userId) || userId <= 0) {
      return false;
    }
    setUpdating(true);
    try {
      await usersService.updateAdminUser(userId, payload);
      addToast('success', '用户资料已更新');
      await load();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setUpdating(false);
    }
  }, [addToast, load, userId]);

  const adjustWallet = useCallback(async (payload: AdminWalletAdjustmentParams) => {
    if (!Number.isFinite(userId) || userId <= 0) {
      return false;
    }
    setUpdating(true);
    try {
      await usersService.adjustAdminUserWallet(userId, payload);
      addToast('success', payload.type === 'credit' ? '加款成功' : '扣款成功');
      await load();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setUpdating(false);
    }
  }, [addToast, load, userId]);

  const releaseAppleVerification = useCallback(async () => {
    if (!Number.isFinite(userId) || userId <= 0) {
      return false;
    }
    setUpdating(true);
    try {
      const result = await usersService.releaseAdminUserAppleVerification(userId);
      setLastReleasedAppleVerification(result);
      addToast('success', 'Apple ID 绑定已解除');
      await load();
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setUpdating(false);
    }
  }, [addToast, load, userId]);

  return {
    data,
    loading,
    error,
    updating,
    lastUpdate,
    lastReleasedAppleVerification,
    reload: load,
    updateStatus,
    updateUser,
    adjustWallet,
    releaseAppleVerification,
  };
}
